/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Key Management Store (Task 10.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { getWorkspaceDomains } from '../lib/urls';

export interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  keyHint: string;
  scopes: string[];
  rateLimitRpm: number;
  monthlySpendLimit: number | null;
  isActive: boolean;
  lastUsedAt: number | null;
  createdAt: number;
}

export interface CreateApiKeyInput {
  name: string;
  environment: 'live' | 'test';
  scopes: string[];
  rateLimitRpm: number;
  monthlySpendLimit?: number | null;
}

export interface NewlyCreatedSecret {
  apiKey: ApiKeyItem;
  secretKey: string;
}

export const SEED_API_KEYS: ApiKeyItem[] = [
  {
    id: 'key-01HXYZ001',
    name: 'Production AI Assistant Bot',
    keyPrefix: 'sk_live_',
    keyHint: 'a1b2',
    scopes: ['text:write', 'chat:write'],
    rateLimitRpm: 120,
    monthlySpendLimit: 100000,
    isActive: true,
    lastUsedAt: Date.now() - 3600000, // 1h ago
    createdAt: Date.now() - 86400000 * 14,
  },
  {
    id: 'key-01HXYZ002',
    name: 'Staging & Integration Agent',
    keyPrefix: 'sk_test_',
    keyHint: 'f4e9',
    scopes: ['*'],
    rateLimitRpm: 60,
    monthlySpendLimit: null,
    isActive: true,
    lastUsedAt: Date.now() - 7200000, // 2h ago
    createdAt: Date.now() - 86400000 * 30,
  },
  {
    id: 'key-01HXYZ003',
    name: 'Media Generation Pipeline',
    keyPrefix: 'sk_live_',
    keyHint: '89c3',
    scopes: ['images:write', 'audio:write'],
    rateLimitRpm: 60,
    monthlySpendLimit: 50000,
    isActive: true,
    lastUsedAt: Date.now() - 86400000 * 2, // 2 days ago
    createdAt: Date.now() - 86400000 * 60,
  },
  {
    id: 'key-01HXYZ004',
    name: 'CAT Translation Sync Service',
    keyPrefix: 'sk_live_',
    keyHint: '2041',
    scopes: ['translations:write', 'translations:read'],
    rateLimitRpm: 300,
    monthlySpendLimit: null,
    isActive: true,
    lastUsedAt: Date.now() - 1800000, // 30m ago
    createdAt: Date.now() - 86400000 * 5,
  },
  {
    id: 'key-01HXYZ005',
    name: 'Legacy V1 Webhook Worker',
    keyPrefix: 'sk_live_',
    keyHint: 'dead',
    scopes: ['text:read'],
    rateLimitRpm: 60,
    monthlySpendLimit: null,
    isActive: false,
    lastUsedAt: Date.now() - 86400000 * 90,
    createdAt: Date.now() - 86400000 * 120,
  },
];

/**
 * Generate Quickstart code snippets for client integration
 */
export function generateCurlSnippet(secretKey: string): string {
  const apiOrigin = getWorkspaceDomains().apiOrigin;
  return `curl -X POST ${apiOrigin}/api/v1/chat/completions \\
  -H "Authorization: Bearer ${secretKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "sophia-fast",
    "messages": [
      { "role": "user", "content": "Hello Sophia Platform!" }
    ]
  }'`;
}

export function generateNodeSnippet(secretKey: string): string {
  return `import { SophiaClient } from '@sofia/sdk';

const client = new SophiaClient({
  apiKey: '${secretKey}',
});

async function main() {
  const response = await client.chat.create({
    model: 'sophia-fast',
    messages: [{ role: 'user', content: 'Hello Sophia Platform!' }],
  });
  console.log(response.choices[0].message.content);
}

main();`;
}

export function generatePythonSnippet(secretKey: string): string {
  return `from sophia import SophiaClient

client = SophiaClient(api_key="${secretKey}")

response = client.chat.create(
    model="sophia-fast",
    messages=[{"role": "user", "content": "Hello Sophia Platform!"}]
)

print(response.choices[0].message.content)`;
}

export interface ApiKeyState {
  keys: ApiKeyItem[];
  isLoading: boolean;
  searchQuery: string;
  statusFilter: 'all' | 'active' | 'revoked';
  isCreateModalOpen: boolean;
  isRevealModalOpen: boolean;
  isRevokeModalOpen: boolean;
  newlyCreatedKey: NewlyCreatedSecret | null;
  keyToRevoke: ApiKeyItem | null;

  // Actions
  createKey: (input: CreateApiKeyInput) => Promise<NewlyCreatedSecret>;
  updateKey: (id: string, updates: Partial<ApiKeyItem>) => Promise<void>;
  revokeKey: (id: string) => Promise<void>;
  toggleKeyActive: (id: string) => void;
  setSearchQuery: (searchQuery: string) => void;
  setStatusFilter: (statusFilter: 'all' | 'active' | 'revoked') => void;
  setCreateModalOpen: (isCreateModalOpen: boolean) => void;
  setRevealModalOpen: (isRevealModalOpen: boolean) => void;
  setRevokeModalOpen: (isRevokeModalOpen: boolean) => void;
  setKeyToRevoke: (key: ApiKeyItem | null) => void;
  clearNewlyCreatedKey: () => void;
  getFilteredKeys: () => ApiKeyItem[];
  getStats: () => {
    total: number;
    active: number;
    revoked: number;
    totalRpm: number;
  };
}

export const useApiKeyStore = create<ApiKeyState>((set, get) => ({
  keys: SEED_API_KEYS,
  isLoading: false,
  searchQuery: '',
  statusFilter: 'all',
  isCreateModalOpen: false,
  isRevealModalOpen: false,
  isRevokeModalOpen: false,
  newlyCreatedKey: null,
  keyToRevoke: null,

  createKey: async (input) => {
    // Generate secure random secret key (sk_live_... or sk_test_...)
    const prefix = input.environment === 'test' ? 'sk_test_' : 'sk_live_';
    const randomHex = Array.from({ length: 48 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
    const secretKey = `${prefix}${randomHex}`;
    const keyHint = randomHex.slice(-4);
    const keyId = `key_${Date.now().toString(36)}${Math.random().toString(36).substr(2, 4)}`;

    const newApiKey: ApiKeyItem = {
      id: keyId,
      name: input.name.trim(),
      keyPrefix: prefix,
      keyHint,
      scopes: input.scopes.length > 0 ? input.scopes : ['text:write', 'chat:write'],
      rateLimitRpm: input.rateLimitRpm || 60,
      monthlySpendLimit: input.monthlySpendLimit || null,
      isActive: true,
      lastUsedAt: null,
      createdAt: Date.now(),
    };

    set((state) => ({
      keys: [newApiKey, ...state.keys],
      isCreateModalOpen: false,
      newlyCreatedKey: { apiKey: newApiKey, secretKey },
      isRevealModalOpen: true,
    }));

    return { apiKey: newApiKey, secretKey };
  },

  updateKey: async (id, updates) => {
    set((state) => ({
      keys: state.keys.map((k) => (k.id === id ? { ...k, ...updates } : k)),
    }));
  },

  revokeKey: async (id) => {
    set((state) => ({
      keys: state.keys.map((k) => (k.id === id ? { ...k, isActive: false } : k)),
      isRevokeModalOpen: false,
      keyToRevoke: null,
    }));
  },

  toggleKeyActive: (id) => {
    set((state) => ({
      keys: state.keys.map((k) => (k.id === id ? { ...k, isActive: !k.isActive } : k)),
    }));
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCreateModalOpen: (isCreateModalOpen) => set({ isCreateModalOpen }),
  setRevealModalOpen: (isRevealModalOpen) => set({ isRevealModalOpen }),
  setRevokeModalOpen: (isRevokeModalOpen) => set({ isRevokeModalOpen }),
  setKeyToRevoke: (keyToRevoke) => set({ keyToRevoke }),

  clearNewlyCreatedKey: () => {
    set({ newlyCreatedKey: null, isRevealModalOpen: false });
  },

  getFilteredKeys: () => {
    const { keys, searchQuery, statusFilter } = get();
    return keys.filter((k) => {
      // 1. Status Filter
      if (statusFilter === 'active' && !k.isActive) return false;
      if (statusFilter === 'revoked' && k.isActive) return false;

      // 2. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = k.name.toLowerCase().includes(q);
        const matchesHint = k.keyHint.toLowerCase().includes(q);
        const matchesPrefix = k.keyPrefix.toLowerCase().includes(q);
        const matchesScope = k.scopes.some((s) => s.toLowerCase().includes(q));
        if (!matchesName && !matchesHint && !matchesPrefix && !matchesScope) return false;
      }

      return true;
    });
  },

  getStats: () => {
    const { keys } = get();
    const active = keys.filter((k) => k.isActive).length;
    const revoked = keys.filter((k) => !k.isActive).length;
    const totalRpm = keys
      .filter((k) => k.isActive)
      .reduce((sum, k) => sum + (k.rateLimitRpm || 0), 0);

    return {
      total: keys.length,
      active,
      revoked,
      totalRpm,
    };
  },
}));
